import { generatePath, useOutletContext } from 'react-router-dom'

import {
  HEALTHY_SUBSCRIPTION_STATUSES,
  TERMINATED_SUBSCRIPTION_STATUSES,
} from '@snootsvet/shared/portal'

import type { PetScopeContext } from '@/customer/components/pet-scope'
import { useGetPetSubscription } from '@/customer/hooks/use-get-pet-subscription'
import { routes } from '@/customer/routes'

export type SubscriptionRequirement = 'no_active_subscription' | 'incomplete' | 'healthy'

const POLL_INTERVAL_MS = 2_000

interface GuardContext {
  petId: string
  status: string | null
}

const guards: Record<SubscriptionRequirement, (ctx: GuardContext) => string | null> = {
  no_active_subscription: ({ status, petId }) => {
    if (status === 'incomplete')
      return generatePath(routes.account.pets.paymentFailed, { patientId: petId })
    if (status && HEALTHY_SUBSCRIPTION_STATUSES.has(status))
      return generatePath(routes.account.pets.welcome, { patientId: petId })
    if (status && !TERMINATED_SUBSCRIPTION_STATUSES.has(status))
      return `${routes.account.pets.index}?pet=${petId}`
    return null
  },
  incomplete: ({ status, petId }) => {
    if (status && HEALTHY_SUBSCRIPTION_STATUSES.has(status))
      return generatePath(routes.account.pets.welcome, { patientId: petId })
    if (status !== 'incomplete') return `${routes.account.pets.index}?pet=${petId}`
    return null
  },
  healthy: ({ status, petId }) => {
    if (!status || !HEALTHY_SUBSCRIPTION_STATUSES.has(status))
      return `${routes.account.pets.index}?pet=${petId}`
    return null
  },
}

/**
 * Returns `null` when any requirement is satisfied (OR semantics).
 * Otherwise returns the redirect path from the first unsatisfied
 * requirement.
 */
function resolveRedirect(require: SubscriptionRequirement[], ctx: GuardContext): string | null {
  const redirects = require.map((req) => guards[req](ctx))
  if (redirects.some((r) => r === null)) return null
  return redirects[0] ?? null
}

/**
 * Evaluates pet subscription-status requirements and returns a
 * redirect path when none are satisfied. Must be used inside
 * `<PetScope />`.
 *
 * Uses OR semantics: returns `null` (no redirect) if any
 * requirement is satisfied.
 */
export function usePetSubscriptionGuard(
  require: SubscriptionRequirement[],
  options?: { poll?: boolean }
): { isPending: boolean; redirect: string | null } {
  const context = useOutletContext<PetScopeContext | null>()
  if (!context?.pet) throw new Error('usePetSubscriptionGuard must be used inside <PetScope />')

  const subscriptionQuery = useGetPetSubscription(context.pet.id, {
    refetchInterval: options?.poll ? POLL_INTERVAL_MS : undefined,
    refetchOnWindowFocus: options?.poll,
  })

  if (subscriptionQuery.isPending) {
    return { isPending: true, redirect: null }
  }

  // On fetch error, status falls to null. This fails open for the
  // `no_active_subscription` guard (allows reaching checkout), but
  // the server rejects duplicate subscriptions with a 409.
  const ctx: GuardContext = {
    petId: context.pet.id,
    status: subscriptionQuery.data?.subscription?.status ?? null,
  }
  return { isPending: false, redirect: resolveRedirect(require, ctx) }
}
